import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Trash2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PostAnnotationsManager from '@/components/posts/PostAnnotationsManager';
import Portal from '@/components/ui/portal';

interface EditPostDialogProps {
  isOpen: boolean;
  onClose: () => void;
  post: {
    id: string;
    content?: string;
    type: 'bulletin' | 'loop';
    image_url?: string;
    thumbnail_url?: string;
    is_artstream?: boolean;
    communities?: Array<{ id: string; name: string; display_name: string }>;
  };
  onSave: (updates: {
    content?: string;
    removedCommunityIds?: string[];
    removeImage?: boolean;
    is_artstream?: boolean;
  }) => Promise<void>;
}

export function EditPostDialog({ isOpen, onClose, post, onSave }: EditPostDialogProps) {
  const [content, setContent] = useState(post.content || '');
  const [removedCommunities, setRemovedCommunities] = useState<Set<string>>(new Set());
  const [removeImage, setRemoveImage] = useState(false);
  const [isArtstream, setIsArtstream] = useState(post.is_artstream ?? false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Reset state when dialog opens/closes or post changes
  useEffect(() => {
    if (isOpen) {
      setContent(post.content || '');
      setRemovedCommunities(new Set());
      setRemoveImage(false);
      setIsArtstream(post.is_artstream ?? false);
    }
  }, [isOpen, post]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        content: content !== post.content ? content : undefined,
        removedCommunityIds: removedCommunities.size > 0 ? Array.from(removedCommunities) : undefined,
        removeImage: removeImage || undefined,
        is_artstream: isArtstream !== (post.is_artstream ?? false) ? isArtstream : undefined,
      });
      
      toast({
        title: 'Post updated',
        description: 'Your changes have been saved.',
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving post:', error);
      toast({
        title: 'Failed to save',
        description: 'There was an error updating your post.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCommunity = (communityId: string) => {
    setRemovedCommunities(prev => {
      const next = new Set(prev);
      if (next.has(communityId)) {
        next.delete(communityId);
      } else {
        next.add(communityId);
      }
      return next;
    });
  };

  const hasChanges = 
    content !== (post.content || '') || 
    removedCommunities.size > 0 || 
    removeImage ||
    isArtstream !== (post.is_artstream ?? false);

  return (
    <AnimatePresence>
      {isOpen && (
        <Portal>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Container to center dialog */}
          <div className="fixed inset-0 z-[1001] flex items-center justify-center px-4">
            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-2xl max-h-[90vh] flex flex-col"
            >
              <div className="rounded-2xl shadow-2xl flex flex-col bg-[#1A1A1B] text-[#D7DADC] border border-[#343536] h-full overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-[#343536] flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Edit Post</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-[#2D2D2F] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6" style={{ minHeight: 0 }}>
                <Tabs defaultValue="description" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-6 bg-[#272729] rounded-md p-1 border border-[#343536]">
                    <TabsTrigger value="description" className="data-[state=active]:bg-[#3A3A3C] data-[state=active]:text-white rounded px-3 py-2 text-sm">Description</TabsTrigger>
                    <TabsTrigger value="media" className="data-[state=active]:bg-[#3A3A3C] data-[state=active]:text-white rounded px-3 py-2 text-sm">Media</TabsTrigger>
                    <TabsTrigger value="annotations" className="data-[state=active]:bg-[#3A3A3C] data-[state=active]:text-white rounded px-3 py-2 text-sm">Annotations</TabsTrigger>
                  </TabsList>

                  {/* Description Tab */}
                  <TabsContent value="description" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="content" className="text-[#D7DADC]">Content</Label>
                      <Textarea
                        id="content"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="What's on your mind?"
                        className="min-h-[200px] resize-none bg-[#0F0F10] border-[#343536] text-[#D7DADC] placeholder:text-gray-400"
                        maxLength={500}
                      />
                      <p className="text-sm text-[#9CA3AF] text-right">
                        {content.length} / 500
                      </p>
                    </div>

                    {/* Artstream Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-[#272729] border border-[#343536]">
                      <div className="flex-1">
                        <Label htmlFor="artstream-toggle" className="text-[#D7DADC] font-medium cursor-pointer">
                          Is it art?
                        </Label>
                        <p className="text-xs text-[#9CA3AF] mt-1">
                          Enable to show this post in Artstream
                        </p>
                      </div>
                      <Switch 
                        id="artstream-toggle"
                        checked={isArtstream} 
                        onCheckedChange={setIsArtstream}
                      />
                    </div>
                  </TabsContent>

                  {/* Communities (now shown under Description) */}
                  {post.communities && post.communities.length > 0 ? (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm text-[#9CA3AF]">Linked communities (click to remove):</p>
                      <div className="flex flex-wrap gap-2">
                        {post.communities.map((community) => {
                          const isRemoved = removedCommunities.has(community.id);
                          return (
                            <Badge
                              key={community.id}
                              variant={isRemoved ? 'outline' : 'default'}
                              className={`cursor-pointer transition-colors ${
                                isRemoved
                                  ? 'bg-transparent text-[#9CA3AF] border border-[#3F3F46] line-through'
                                  : 'bg-[#2D2D2F] text-[#E5E7EB] border border-[#3F3F46] hover:bg-[#3A3A3C]'
                              }`}
                              onClick={() => toggleCommunity(community.id)}
                            >
                              {community.display_name || community.name}
                              {isRemoved && <X className="w-3 h-3 ml-1" />}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-[#9CA3AF]">No communities linked to this post</p>
                  )}

                  {/* Media Tab */}
                  <TabsContent value="media" className="space-y-4">
                    {post.image_url || post.thumbnail_url ? (
                      <div className="space-y-4">
                        <div className="relative rounded-lg overflow-hidden bg-[#0F0F10] border border-[#343536]">
                          <img
                            src={post.thumbnail_url || post.image_url}
                            alt="Post media"
                            className="w-full h-auto"
                          />
                          {removeImage && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <p className="text-white font-medium">Will be removed</p>
                            </div>
                          )}
                        </div>
                        
                        <Button
                          variant={removeImage ? "secondary" : "destructive"}
                          onClick={() => setRemoveImage(!removeImage)}
                          className="w-full"
                        >
                          {removeImage ? (
                            <>
                              <ImageIcon className="w-4 h-4 mr-2" />
                              Keep Image
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove Image
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-[#9CA3AF] text-center py-8">
                        No media attached to this post
                      </p>
                    )}
                  </TabsContent>

                  {/* Annotations Tab */}
                  <TabsContent value="annotations" className="space-y-4">
                    <PostAnnotationsManager postId={post.id} postType={post.type} />
                  </TabsContent>
                </Tabs>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-[#343536] flex items-center justify-between bg-[#1A1A1B]">
                <p className="text-sm text-[#9CA3AF]">
                  {hasChanges ? 'You have unsaved changes' : 'No changes'}
                </p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={onClose} className="border-[#4B5563] text-[#D7DADC]">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                  >
                    {isSaving ? (
                      <>Saving...</>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
              </div>
            </motion.div>
          </div>
        </Portal>
      )}
    </AnimatePresence>
  );
}
